import { Enzyme } from '../data/enzymeLibrary';

// 定义切割位点的数据结构
export interface CutSite {
  enzymeName: string;
  cutPosition: number; // 在完整序列中的绝对位置
  recognitionSite: string;
}

// 规范化输入序列：支持 FASTA（去除以 '>' 开头的头部行）、去除非字母字符，统一大写，并将 U 视为 T
export function normalizeSequenceInput(sequence: string, treatUasT: boolean = true): string {
  const noHeader = sequence.replace(/^>.*$/gm, '');
  const lettersOnly = noHeader.replace(/[^A-Za-z]/g, '');
  const upper = lettersOnly.toUpperCase();
  return treatUasT ? upper.replace(/U/g, 'T') : upper;
}

// 自动检测序列类型
export function detectSequenceType(sequence: string): 'protein' | 'nucleic' | 'unknown' {
  // 1. Sanitize the input（支持 FASTA）
  const sanitizedSeq = normalizeSequenceInput(sequence);
  if (!sanitizedSeq) {
    return 'unknown';
  }

  // 2. Define character sets for decision making
  // These characters are ONLY found in proteins, not in any standard or ambiguous nucleic acid codes.
  const proteinExclusiveChars = new Set(['E', 'F', 'I', 'L', 'P', 'Q']);
  
  // The full set of valid nucleic acid characters, including IUPAC ambiguity codes.
  const validNucleicChars = new Set(['A', 'C', 'G', 'T', 'U', 'N', 'R', 'Y', 'S', 'W', 'K', 'M', 'B', 'D', 'H', 'V']);
  
  // The full set of 20 standard amino acids.
  const validProteinChars = new Set(['A', 'R', 'N', 'D', 'C', 'Q', 'E', 'G', 'H', 'I', 'L', 'K', 'M', 'F', 'P', 'S', 'T', 'W', 'Y', 'V']);

  // 3. First pass: check for any protein-exclusive characters.
  let hasProteinExclusive = false;
  for (const char of sanitizedSeq) {
    if (proteinExclusiveChars.has(char)) {
      hasProteinExclusive = true;
      break;
    }
  }

  // 4. Decision based on findings
  if (hasProteinExclusive) {
    // If it has a protein-exclusive character, it MUST be a protein.
    // Now, we just validate that all characters are valid protein characters.
    for (const char of sanitizedSeq) {
      if (!validProteinChars.has(char)) {
        return 'unknown'; // Contains a mix of protein and invalid characters
      }
    }
    return 'protein';
  } else {
    // If no protein-exclusive characters are found, it's either nucleic acid or an ambiguous protein.
    // The common heuristic is to check if it fits the nucleic acid alphabet.
    for (const char of sanitizedSeq) {
      if (!validNucleicChars.has(char)) {
        return 'unknown'; // Contains characters that are neither nucleic nor protein-exclusive.
      }
    }
    // If all characters are valid nucleic acid characters, classify it as nucleic.
    return 'nucleic';
  }
}

// 查找所有切割位点
export function findCutSites(sequence: string, enzymes: Enzyme[]): CutSite[] {
  const sites: CutSite[] = [];
  const seqUpper = normalizeSequenceInput(sequence); // 支持 FASTA/多行输入

  if (!seqUpper) return [];

  for (const enzyme of enzymes) {
    try {
      // 准备正则表达式模式
      // 1. 检查是否已经包含方括号等特殊字符
      const isAlreadyRegex = /[\[\]\(\)\|\.\*\+\?\^\\]/.test(enzyme.recognitionSite);
      
      let pattern: string;
      if (isAlreadyRegex) {
        // 如果已经包含正则表达式特殊字符，直接使用
        pattern = enzyme.recognitionSite;
      } else {
        // 如果是普通序列，将其转换为严格匹配的模式
        // 例如 'GAATTC' 会被解释为普通字符串，而不是正则表达式
        pattern = enzyme.recognitionSite;
      }
      // 支持跨收尾匹配：将序列尾部拼接开头的一段
      const recLen = Math.max(1, enzyme.recognitionSite.length);
      const ext = Math.max(0, recLen - 1);
      const seqToSearch = seqUpper + seqUpper.slice(0, ext);

      // 使用 'g' flag 来查找所有匹配项，'i' flag 使匹配不区分大小写
      const regex = new RegExp(pattern, 'gi');

      let match;
      while ((match = regex.exec(seqToSearch)) !== null) {
        // 避免零宽度匹配导致的无限循环
        if (match.index === regex.lastIndex) {
          regex.lastIndex++;
          continue;
        }
        // 计算切割位置，考虑跨越边界的情况
        const cutPos0 = match.index + enzyme.cutPosition;
        const crossesBoundary = match.index + recLen > seqUpper.length;
        let cutPos = cutPos0;
        if (crossesBoundary) {
          const mod = cutPos0 % seqUpper.length;
          cutPos = mod === 0 ? seqUpper.length : mod;
        }
        if (cutPos >= 1 && cutPos <= seqUpper.length) {
          sites.push({
            enzymeName: enzyme.name,
            cutPosition: cutPos,
            recognitionSite: match[0],
          });
        }
      }
    } catch (e) {
      console.error(`Error processing enzyme ${enzyme.name} with pattern ${enzyme.recognitionSite}:`, e);
    }
  }

  // 按位置排序并去重
  return sites
    .filter((site, index, self) => 
      index === self.findIndex(s => 
        s.cutPosition === site.cutPosition && s.enzymeName === site.enzymeName
      )
    )
    .sort((a, b) => a.cutPosition - b.cutPosition);
} 